'use client';

import { Fragment } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, type DotProps } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../chart';

export interface RadarDotsSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface RadarDotsChartProps {
  data: RadarDotsSlice[];
  showLegend?: boolean;
  height?: number;
  className?: string;
}

function ColoredDot(props: DotProps & { payload?: RadarDotsSlice }) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={4} fill={payload?.color} fillOpacity={1} stroke="none" />;
}

export function RadarDotsChart({
  data,
  showLegend = true,
  height = 300,
  className,
}: RadarDotsChartProps) {
  const config: ChartConfig = Object.fromEntries(
    data.map((d) => [d.key, { label: d.label, color: d.color }]),
  );

  const LegendContent = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '4px 16px',
        paddingTop: 8,
      }}
    >
      {data.map((item) => (
        <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 2,
              background: item.color,
              flexShrink: 0,
            }}
          />
          <span className="text-[var(--color-text-secondary,#71717a)]">{item.label}</span>
          <span className="font-bold text-[var(--color-text-primary,#18181b)]">{item.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Fragment>
      <ChartContainer config={config} className={className} style={{ height }}>
        <RadarChart data={data} margin={{ top: 8, right: 20, bottom: 8, left: 20 }}>
          <ChartTooltip content={<ChartTooltipContent nameKey="key" />} />
          <PolarGrid stroke="var(--color-border-subtle, rgba(0,0,0,0.12))" />
          <PolarAngleAxis dataKey="label" tick={{ fontSize: 12 }} />
          <Radar
            dataKey="value"
            fill="var(--color-brand-500, #29a9e0)"
            fillOpacity={0.35}
            stroke="var(--color-brand-500, #29a9e0)"
            dot={<ColoredDot />}
          />
        </RadarChart>
      </ChartContainer>
      {showLegend && <LegendContent />}
    </Fragment>
  );
}
